/**
 * THE PORTFOLIO READ LAYER — one row per project, derived, never typed.
 *
 * The Portfolio screen shows every project on the tenant on one page, for the
 * weekly meeting. Each row's Health / Progress / Waiting-on / Go-live / Budget /
 * Moved figures are DERIVED here from the project's own board data — the same
 * pure helpers the Plan and Insights screens already read (phases, blockers,
 * budget rollup), run once per board. Nothing in a row is a stored "pulse": open
 * the page and it reads the truth as it stands (v1 is on-demand; a persisted
 * snapshot is a later increment).
 *
 * HEALTH IS THE WORST OF THREE, each read from the project itself:
 *   · Schedule — the go-live vs today, plus any phase now overdue.
 *   · Budget   — the forecast vs the baseline (±5% green, >5% amber, >12% red).
 *   · Blockers — the oldest open blocker's age (none/under-a-week green, over a
 *                week amber, over two weeks red).
 * On hold is its OWN state, not a colour: a parked project is hushed from the
 * at-risk view but never shows green.
 *
 * This module is pure and has no store/React imports, so it can be tested on
 * plain fixtures and run per board in a loop.
 */
import type { Issue, IssueStatus, BlockerType, CostBasis } from './types';
import { isBacklog } from './types';
import {
  type Project,
  type BudgetContext,
  rollupBudget,
} from './projects';
import {
  type Phase,
  type PhaseStatus,
  type GoLiveState,
  livePhases,
  phaseShownStatus,
  goLiveState,
  goLiveAnchorDay,
  goLiveIsPlanAnchored,
  phaseCardCounts,
  daysPast,
} from './phases';
import { reallyBlocked, blockerOf } from './blocked';
import { ageInDays, isWorking, workingAges, agingThreshold } from './card-age';
import { richHasText, type RichDoc } from './rich-text';
import type { CharterRisk } from './charter';
import type { MilestoneRow } from './milestones';
import { type IssueType } from './issue-types';
import { boardInvestmentMix, investmentLifecycleDefault, type InvestmentMix } from './investment';
import { isContinuous, type LifecycleTier } from './lifecycle';
import { isInService, goLiveOverdue } from './handoff';
import { healthOfIssue, scheduleHealth, scheduleHealthSeverity, expectedPctOfWindow, type ScheduleHealth } from './progress-health';
import { DEFAULT_CALENDAR, workingDaysInclusive, type WorkCalendar } from './work-calendar';
import { spendTrend, paidSpendSteps } from './cost-trend';
import { earnedSchedule, type EarnedSchedule } from './earned-schedule';
import { effectiveProgressPct } from './progress-derive';
import type { DeliverySnapshot } from './types';

/** The three grades a signal can take. `hold` is not a grade — it is the
 *  project-level parked state, carried on the row separately. */
export type Rag = 'green' | 'amber' | 'red';

/** One health sub-signal: its grade and the one short reason behind it. */
export interface Signal {
  rag: Rag;
  why: string;
}

/** An open thing the project is waiting on — a person, a decision, an external
 *  party, a parked reason. Oldest first in a row. */
export interface WaitingItem {
  type: BlockerType | '';
  /** What it waits on, in a few words (the blocker text, or the named target). */
  label: string;
  /** The named target when there is one (a person, a card ref). */
  target?: string;
  /** How long it has waited, in days — null when the start was never stamped. */
  days: number | null;
  /** The blocked card this came from — its reference and title — so the cards
   *  drill can show the real card. Absent on the project-level parked item. */
  ref?: string;
  title?: string;
}

/**
 * HOW WORK MOVED in the window — the directional flow read off the card
 * transition log (`activity`), not a "touched" count. Each is a count of moves
 * in the window: cards that entered work, reached review, closed, went backwards
 * (a demotion or a reopen), and new blockers raised. Read the log for the first
 * three and the last is milestone-based; `closed` is the card's own DB-stamped
 * finish (`completedAt`) so it agrees with the throughput trend to the number.
 */
export interface MovementCounts {
  started: number;
  /** Moved INTO In Staging in the window. Only ever non-zero on a board that
   *  turned the column on, so it surfaces (verb + chip omit a zero) exactly
   *  there and nowhere else — the stat is considered only when staging is in use. */
  staged: number;
  toReview: number;
  closed: number;
  pushedBack: number;
  newBlockers: number;
}

/** The closed-per-week trend behind a project — the most-read flow number. A
 *  Linear-style single line, never a cumulative-flow diagram. `weekly` is oldest
 *  → newest with the last entry being this week; `norm` is the average of the
 *  earlier weeks, the line the current week is read against. Absent when nothing
 *  has closed in the window (no line to draw, no norm to judge). */
export interface Throughput {
  weekly: number[];
  thisWeek: number;
  norm: number;
  dir: 'up' | 'down' | 'flat';
}

/** One in-flight card's age against the board's own norm — the aging-WIP signal
 *  (card-age.ts) surfaced at the portfolio: pressing while within the norm,
 *  stalled once past it. */
export interface AgingCard {
  ref: string;
  title: string;
  days: number;
  aged: boolean;
}

/** One project as the portfolio reads it. Everything here is derived. */
export interface PortfolioRow {
  id: string;
  name: string;
  owner: string;
  /** The project sponsor, when one is named — shown on the status report head. */
  sponsor?: string;
  /** The planned start as a plain `YYYY-MM-DD` day, when set. */
  startDay?: string;
  onHold: boolean;
  holdReason?: string;
  holdPickup?: string;
  /** Whether this board is actually being RUN AS A PROJECT — it has a plan set up:
   *  a go-live, at least one phase, or a budget. A bare development/kanban board
   *  (none of those) is `false`: it tracks work but is not a project a steering
   *  meeting reviews, so it is kept out of the health counts, the budget total and
   *  the pack. The distinction is data, not a mode the user picks — a board becomes
   *  a project the moment it is given any of the three. */
  tracked: boolean;
  /** The headline — worst of the three signals. `null` on a held project (its
   *  colour is hushed; read `onHold` instead). */
  health: Rag | null;
  /** The one short phrase under the health pill, e.g. "blocked + late". */
  healthWhy: string;
  schedule: Signal;
  /** Null when there is no forecast to judge the baseline against. */
  budget: Signal | null;
  blockers: Signal;
  /** 0–100, cards done across the live phases (the legacy done-count rollup the Plan
   *  headline also shows). Kept for callers that read it; the headline Project %
   *  shown on the report and the roll-up row is `projectPct` below. */
  progress: number;
  /** THE PROJECT'S PERCENT-COMPLETE (#progress), count-weighted and GRANULAR: every
   *  non-cancelled card counts equally, a done card in full, an open card the share
   *  it records (typed %, or its ticked definition of done). Reaches 100 only when
   *  every counted card is done. This is the figure a PM (and the vendor) reads. */
  projectPct: number;
  /** How that % reads against the plan's calendar, in plain words — "On schedule",
   *  "About 15% behind schedule", "Past its window". `kind: 'none'` (empty label)
   *  when the project has no start→go-live window to judge against; distinct from
   *  the `schedule` go-live RAG above, which stays the headline signal. */
  progressSchedule: ScheduleHealth;
  /** Every open blocker, oldest first. */
  waiting: WaitingItem[];
  /** The go-live as planned (anchor-resolved day) and as now forecast (phase-projected). */
  planDay?: string;
  forecastDay?: string;
  /** How the go-live is anchored to the plan (0217), in plain words — "ends Deployment phase" or
   *  the milestone name — so every surface can name what the go-live rides on. Absent for a
   *  fixed/unanchored date. */
  goLiveAnchorLabel?: string;
  /** Days the forecast go-live slips past the plan (0 when on plan or unknown). */
  slipDays: number;
  /** The approved baseline, and the estimate-at-completion. */
  budgetBase?: number;
  forecast?: number;
  variance?: { diff: number; pct: number };
  /** Committed (raised, unpaid) and actual (paid) to date, from the ledger, in
   *  the base currency — the report's "Committed + actual". */
  committed: number;
  actual: number;
  /** The decision-tier earned-value read (CPI/SPI + a derived EAC), from the budget,
   *  the progress and the spend — null when there is no baseline budget or no progress
   *  to earn against. See {@link earnedValue}; this is why the budget read no longer
   *  has to trust a hand-typed forecast. */
  evm: EarnedValue | null;
  /** The date-truth schedule read (Earned Schedule / SPI(t), Layer C) — read off the
   *  plan's own value curve over the cards that carry a planned due, so it tells the
   *  truth about the FINISH DATE where the money SPI drifts to 1.0. Null when there is
   *  no start, no committed finish, or nothing dated to read. See {@link earnedSchedule};
   *  pairs with `evm` on Surface 6A. */
  earnedSchedule: EarnedSchedule | null;
  /** The phases, in order, with where each stands against the go-live. `targetEnd`
   *  is the phase's planned finish (`YYYY-MM-DD`), carried so a milestones table can
   *  read the plan and measure an active phase's lateness against it — no per-phase
   *  forecast is invented. */
  phases: { id: string; name: string; status: PhaseStatus; goLive: GoLiveState; targetEnd?: string }[];
  /** What changed since the reference date — the directional verbs, or empty for
   *  "no movement". Derived from `movement` for the roll-up cell. */
  moved: string[];
  /** The directional flow counts behind `moved` — read by the status report,
   *  which shows each verb as its own figure. */
  movement: MovementCounts;
  /** The closed-per-week trend, when anything has closed in the window. */
  throughput?: Throughput;
  /** The cards in flight with their age against the board's norm, oldest first —
   *  the "pressing vs stalled" read. Empty on a held project. */
  aging: AgingCard[];
  /** The board's own "in flight too long" line, in days (its 75th percentile,
   *  floored) — so the report can say what "stuck" measures rather than imply a
   *  fixed threshold. */
  agingNorm: number;
  /** The project's captured risks (free text) and success measure (rich text,
   *  0134), surfaced on the status report so the capture is not dead weight —
   *  every steering report carries a risks read. Absent when the project set
   *  neither. */
  risks?: string;
  successMeasure?: RichDoc | null;
  /** The project's charter Risk register, read as rows (charter.ts) so the pack
   *  and the drill-down can carry a risks read straight from the charter instead
   *  of leaving it stranded in the document. Absent when the charter has no
   *  register or none was authored. */
  charterRisks?: CharterRisk[];
  /** This board's Run-vs-Change work mix, by card count (#435) — each card's
   *  effective investment class rolled up through the board's own types. Drives the
   *  per-board Mix column, and the portfolio-wide band sums these across rows
   *  (`combineInvestmentMix`). `label` is null on a board with no cards to classify. */
  mix: InvestmentMix;
  /** The board's short key (ref prefix, e.g. NET), for the compact "NET · Change"
   *  identity line in the lifecycle lens. Empty when the board has no key set. */
  key?: string;
  /** WHERE THIS BOARD IS IN ITS LIFE (#435 surface 2) — the explicit go-live
   *  handoff, not a guess. `inservice` once the board has been handed over
   *  (`actualGoLive` set) OR is run as a continuous service; `indelivery` for a
   *  tracked project still being built; `pipeline` for a bare board. The Portfolio
   *  bands its rows by this. */
  tier: LifecycleTier;
  /** The board's chosen delivery shape and the frozen handoff, carried straight
   *  from the board row so the portfolio need not re-derive them. */
  deliveryMode?: 'project' | 'continuous';
  /** How the vendor(s) are paid — tunes how the cost tile reads CPI vs the forecast
   *  verdict (see costBasisRead). Absent = the balanced read. */
  costBasis?: CostBasis;
  actualGoLive?: number;
  deliverySnapshot?: DeliverySnapshot;
  /** How overdue an in-delivery project is for its handoff — the pull-adoption
   *  nudge ("Go-live passed 11 days ago — mark it live"). Null unless the planned
   *  go-live has passed on an un-handed-over project. */
  overdue?: { days: number; level: 'amber' | 'red' };
  /** The 28-day flow read an in-service board is judged on, not a schedule:
   *  `perWeek` cards finished, `net` = raised − finished (negative = closing faster
   *  than arriving). Null when nothing has moved in the window. */
  flow28?: { perWeek: number; net: number };
  /** The percent-complete read rolled up across this board's open cards (#progress):
   *  how many cards carry a % that is lagging the calendar (`behind`) and how many
   *  the pace projects will miss their due date (`atRisk`, incl. overdue), out of
   *  `tracked` cards that have both a % and a schedule to judge it against. The
   *  Portfolio shows these as health counts under the delivery bar. All zero when no
   *  card on the board tracks a %. */
  progressCounts: { behind: number; atRisk: number; tracked: number };
  /** The actual cards behind that roll-up — every tracked card whose progress reads
   *  behind / at-risk / overdue (on-track, done, none and tracking excluded), so the
   *  status-report drill-down can list what is slipping, not just count it. Built with
   *  the SAME `healthOfIssue` engine as `progressCounts`, so the list can never
   *  disagree with the counts. Empty when nothing is slipping. */
  progressCards: ProgressCard[];
}

/** One slipping card, for the status report's Progress drill-down (#progress). The
 *  %-bar inputs and the health read a `ProgressMini` + label render from, paired with
 *  the card's own ref/title/due so the reader can find it. `kind` is the health band
 *  (behind, or at-risk incl. overdue); `label` is its word ("Behind" / "Won't make
 *  it" / "Overdue"). */
export interface ProgressCard {
  ref: string;
  title: string;
  /** The recorded %, 0..100 — always set for a behind/at-risk card. */
  pct: number;
  /** What the calendar expects by now (0..100) — so the drill can show the gap
   *  ("40% · expected ~90%") in words, not only as the bar's tick. Null only when
   *  there is no schedule, which a judged card always has. */
  expectedPct: number | null;
  kind: 'behind' | 'at-risk';
  label: string;
  /** The target day (YYYY-MM-DD) — always present for a judged card. */
  dueDate?: string;
}

/** Roll the per-card progress read up across a board's OPEN cards: a card counts
 *  only once it carries a % and a start/due to judge it against (kind behind or
 *  at-risk). Pure — the same `healthOfIssue` every card surface uses, so the
 *  portfolio can never disagree with a card's own bar. */
function progressCountsOf(
  issues: Issue[],
  phases: Phase[],
  nowMs: number,
  doneChecks: readonly string[] = [],
  cal: WorkCalendar = DEFAULT_CALENDAR,
): { behind: number; atRisk: number; tracked: number } {
  const phaseById = new Map(phases.map((p) => [p.id, p]));
  let behind = 0;
  let atRisk = 0;
  let tracked = 0;
  for (const i of issues) {
    // The process starts at To-Do — a backlog card is a parked idea, never counted as
    // behind / at-risk, even if a date was set on it (Vinnie, 3 Nov).
    if (isBacklog(i.status)) continue;
    // Judge the card on its EFFECTIVE % — a typed % wins, else the card's own
    // checklist, else the board's ticked Definition of Done — the SAME number the
    // card's own bar shows (effectiveProgress), so the portfolio can never disagree
    // with a card. Without this the counts saw only a typed %, and a card measured
    // by its checklist or the DoD read as unjudged and fell out of the roll-up.
    // A phased, date-less card is still judged against its phase's window.
    const h = healthOfIssue(
      { ...i, progressPct: effectiveProgressPct(i, doneChecks) },
      nowMs,
      i.phase ? phaseById.get(i.phase) : undefined,
      cal,
    );
    if (h.kind === 'none' || h.kind === 'tracking' || h.kind === 'done') continue;
    tracked += 1;
    if (h.kind === 'behind') behind += 1;
    else if (h.kind === 'at-risk') atRisk += 1;
  }
  return { behind, atRisk, tracked };
}

/** The cards BEHIND that roll-up — the tracked cards whose progress reads behind or
 *  at-risk (overdue included, since the engine folds overdue into at-risk). On-track,
 *  done, none and tracking cards carry no signal and are left out. Same
 *  `healthOfIssue` read as {@link progressCountsOf}, so the list and the counts are
 *  always in step. */
function progressCardsOf(
  issues: Issue[],
  phases: Phase[],
  nowMs: number,
  doneChecks: readonly string[] = [],
  cal: WorkCalendar = DEFAULT_CALENDAR,
): ProgressCard[] {
  const phaseById = new Map(phases.map((p) => [p.id, p]));
  const out: ProgressCard[] = [];
  for (const i of issues) {
    // Backlog excluded here too, so this drill-down list matches the counts above
    // (both start at To-Do) and can never disagree.
    if (isBacklog(i.status)) continue;
    // Same effective-% read and phase-window fallback as the counts, so the
    // drill-down list, the counts above and the card's own bar are built from the
    // identical number and can never disagree.
    const phase = i.phase ? phaseById.get(i.phase) : undefined;
    const h = healthOfIssue({ ...i, progressPct: effectiveProgressPct(i, doneChecks) }, nowMs, phase, cal);
    if (h.kind !== 'behind' && h.kind !== 'at-risk') continue;
    // The target the read judged against — the card's own due, or the phase's
    // targetEnd it fell back to — so a phase-judged card still names a date.
    const effectiveDue = i.dueDate || phase?.targetEnd || undefined;
    out.push({
      ref: i.ref,
      title: i.title,
      pct: h.pct ?? 0,
      expectedPct: h.expectedPct,
      kind: h.kind,
      label: h.label,
      ...(effectiveDue ? { dueDate: effectiveDue } : {}),
    });
  }
  // Worst first — overdue, then won't-make-it (both at-risk), then behind; within a
  // band the earlier due date is the more pressing, so it leads. The reader meets
  // the cards that will miss their date before the ones merely lagging the calendar.
  const rank = (c: ProgressCard): number => (c.label === 'Overdue' ? 0 : c.kind === 'at-risk' ? 1 : 2);
  out.sort((a, b) => rank(a) - rank(b) || (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
  return out;
}

/** A plain `YYYY-MM-DD` day as local-midnight ms — so a day difference can be
 *  measured. Matches `daysPast`'s own parsing, so the two never drift. */
const dayToMs = (day?: string): number | undefined => {
  if (!day) return undefined;
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d).getTime();
};

/** A project's planned go-live as its plain UTC day — the day the Plan shows and the phase rows
 *  read against, so the portfolio can never disagree with the Plan. Resolves the 0217 anchor
 *  (phase end / milestone day / fixed typed date) through the one shared resolver. */
const goLiveDayOf = (
  project: Pick<Project, 'goLive' | 'goLiveAnchor'>,
  phases: readonly Pick<Phase, 'id' | 'targetEnd'>[] = [],
  milestoneDay?: (id: string) => string | null | undefined,
): string | undefined => goLiveAnchorDay(project, phases, milestoneDay);

/** How the go-live is anchored, in plain words, for the surfaces that name it (0217): a phase
 *  anchor reads "ends {Phase} phase", a milestone anchor the milestone name. A fixed/unanchored
 *  date has no plan anchor, so it returns undefined. */
const goLiveAnchorLabelOf = (
  project: Pick<Project, 'goLiveAnchor'>,
  phases: readonly Pick<Phase, 'id' | 'name'>[],
  milestones: readonly MilestoneRow[],
): string | undefined => {
  const a = project.goLiveAnchor;
  if (a?.kind === 'phase' && a.id) {
    const ph = phases.find((p) => p.id === a.id);
    return ph ? `ends ${ph.name} phase` : undefined;
  }
  if (a?.kind === 'milestone' && a.id) return milestones.find((m) => m.id === a.id)?.name;
  return undefined;
};

/** A project's start date (ms) as its plain UTC day, the same way `goLiveDayOf`
 *  reads the go-live — so the two dates never parse differently. */
export const startDayOf = (project: Pick<Project, 'startDate'>): string | undefined =>
  project.startDate != null ? new Date(project.startDate).toISOString().slice(0, 10) : undefined;

/** The status a phase actually SHOWS — the card-derived status, or a manual
 *  override that outranks it. The one canonical rule (phaseShownStatus), shared
 *  with the Plan and the go-live handoff so no two surfaces ever disagree. */
const shownStatusOf = (phase: Phase, issues: Issue[]): PhaseStatus => phaseShownStatus(phase, issues);

/** Worst grade wins. */
const RANK: Record<Rag, number> = { green: 0, amber: 1, red: 2 };
const worst = (rags: Rag[]): Rag =>
  rags.reduce<Rag>((acc, r) => (RANK[r] > RANK[acc] ? r : acc), 'green');

/**
 * SCHEDULE — the go-live vs today, plus any phase now overdue. The plan is the
 * stored go-live; the forecast is the latest target-end of a phase still open,
 * when that runs past the plan. A go-live day already passed with work unfinished
 * is the alarm (red); a forecast that slips more than three weeks past the plan
 * is red too; any slip, or a phase past its own target, is amber.
 *
 * A project whose go-live day has passed but that was never handed over to
 * service — even with its delivery phases all done — is NOT "on plan": it is
 * overdue to go live. That reads amber from the go-live day and red once it is
 * {@link GO_LIVE_OVERDUE_RED_DAYS} days past, the same thresholds the portfolio's
 * "mark it live" nudge uses, so the steering report and the nudge never disagree.
 */
export function scheduleSignal(
  project: Project,
  phases: Phase[],
  issues: Issue[],
  now = Date.now(),
  /** Resolve a milestone id to its planned day, so a go-live anchored to a milestone reads its
   *  date (0217). Omitted → phase/fixed anchors still resolve; a milestone anchor falls back to
   *  the typed date. The caller (buildPortfolio) passes the board's milestone register. */
  milestoneDay?: (id: string) => string | null | undefined,
): Signal & {
  planDay?: string;
  forecastDay?: string;
  slipDays: number;
} {
  const live = livePhases(phases);
  const planDay = goLiveDayOf(project, phases, milestoneDay);
  // A plan-anchored go-live derives its date from the work, so a later phase is post-launch
  // work, not a breach — goLiveState reads calm for it.
  const planAnchored = goLiveIsPlanAnchored(project.goLiveAnchor);
  const states = live.map((p) => goLiveState(p, planDay, shownStatusOf(p, issues), now, planAnchored));
  const pastOpen = states.includes('past-open');

  // The forecast go-live: the latest target-end among unfinished phases, when it
  // runs past the plan. Target-ends are plain `YYYY-MM-DD` days, so the latest is
  // the lexical max. With no targets, or nothing past the plan, forecast = plan.
  let maxTarget: string | undefined;
  for (const p of live) {
    if (shownStatusOf(p, issues) === 'done') continue;
    if (!p.targetEnd) continue;
    // A plan-anchored go-live derives its date from the work, so a phase PLANNED to end after
    // it is legitimate post-launch (hypercare) work, not a slip — `goLiveState` already reads
    // it calm 'post-go-live', and the forecast/slip must agree (0217). Exclude it here. A fixed
    // or committed go-live is unchanged: a phase planned past it still counts as a breach.
    if (planAnchored && planDay && p.targetEnd > planDay) continue;
    if (!maxTarget || p.targetEnd > maxTarget) maxTarget = p.targetEnd;
  }
  const forecastDay =
    maxTarget && planDay ? (maxTarget > planDay ? maxTarget : planDay) : (maxTarget ?? planDay);
  const slipDays =
    planDay && forecastDay && forecastDay > planDay ? daysPast(planDay, dayToMs(forecastDay)) : 0;

  const phaseOverdue = live.some(
    (p) => shownStatusOf(p, issues) === 'active' && daysPast(p.targetEnd, now) > 0,
  );

  // Overdue to go live: the plan day is behind us and the project was never
  // handed over to service. `pastOpen` (above) is the sharper case — work still
  // open past go-live — so it wins; this branch catches the quieter, easier-to-
  // miss case the owner hit: delivery phases all done, go-live a month gone, yet
  // the board still reads a live project because nobody marked it live.
  const overdue = goLiveOverdue({
    deliveryMode: project.deliveryMode,
    tracked: true, // scheduleSignal is only ever asked of a tracked project.
    actualGoLive: project.actualGoLive,
    // Measure "overdue to go live" against the ANCHOR-resolved go-live day (0217), not the raw
    // typed date, so a phase-anchored project is judged by the date the plan actually sets.
    plannedGoLive: planDay ? Date.parse(`${planDay}T00:00:00Z`) : project.goLive,
    nowMs: now,
  });

  let rag: Rag = 'green';
  let why = 'on plan';
  if (pastOpen) {
    rag = 'red';
    why = 'Go-Live passed, work open';
  } else if (overdue) {
    rag = overdue.level;
    why =
      overdue.days <= 0
        ? 'Go-Live reached, not yet live'
        : `Go-Live passed ${overdue.days} day${overdue.days === 1 ? '' : 's'} ago, not yet live`;
  } else if (slipDays > 21) {
    rag = 'red';
    why = `slips ${slipDays} days`;
  } else if (slipDays > 0) {
    rag = 'amber';
    why = `slips ${slipDays} days`;
  } else if (phaseOverdue) {
    rag = 'amber';
    why = 'a phase is overdue';
  } else if (states.includes('approaching')) {
    rag = 'amber';
    why = 'Go-Live approaching';
  }
  return { rag, why, planDay, forecastDay, slipDays };
}

/**
 * BUDGET — the forecast vs the baseline. Within ±5% reads green; more than 5%
 * over is amber; more than 12% over is red; more than 5% under names the saving.
 * Null when there is no forecast to judge against (the row shows no budget grade).
 * Matches the Plan's own budget verdict to the decimal.
 */
export function budgetSignal(project: Project): Signal | null {
  if (project.forecast == null || project.budget == null || project.budget <= 0) return null;
  const pct = ((project.forecast - project.budget) / project.budget) * 100;
  // Whole percent, to match the roll-up cell and the report's Variance line — the
  // same overrun must not read "9.2% over" here and "9% over" three lines down.
  const mag = Math.abs(pct).toFixed(0);
  if (pct > 12) return { rag: 'red', why: `${mag}% over` };
  if (pct > 5) return { rag: 'amber', why: `${mag}% over` };
  if (pct < -5) return { rag: 'green', why: `${mag}% under` };
  return { rag: 'green', why: 'on budget' };
}

/**
 * EARNED VALUE — the decision-tier cost/schedule read a PMO expects, DERIVED from
 * numbers FlightDeck already captures, never a hand-typed field (29 Oct, the pm-witness
 * audit). This is the ratio layer (CPI/SPI + a derived EAC), not a finance ledger:
 *
 *   BAC  baseline budget (budget at completion)        = project.budget
 *   EV   earned value, the worth of the work done       = BAC × %complete
 *   AC   actual cost, what has been SPENT               = actual (NOT committed — see B)
 *   PV   planned value, the worth due by now            = BAC × expected-%-by-now
 *   CPI  cost performance  EV/AC   (<1 over cost)
 *   SPI  schedule performance EV/PV (<1 behind)
 *   EAC  estimate at completion  BAC/CPI  (the forecast the manual field could not give)
 *   ETC  estimate to complete    EAC − AC
 *
 * TWO CORRECTIONS (Vinnie, 29 Oct), after a TT$480k-baseline project read a TT$3.6M EAC at
 * 6% complete:
 *   (A) HOLD the cost forecast + verdict below EVM_COST_FORECAST_MIN_PCT complete — BAC/CPI
 *       from a tiny earned sample is a wild guess (cpi/eac/etc/cpiRag go null; `costReady`
 *       says so). The raw baseline / committed / spent still show.
 *   (B) AC is ACTUAL SPEND only, not committed+actual: committed money is for work not yet
 *       earned, so counting it against a small early EV craters CPI. Committed is reported
 *       separately (`committed`), never inside the ratio.
 *
 * The DERIVED EAC is why this exists: `budgetSignal` grades a MANUAL `project.forecast`,
 * which on a seeded board defaulted to actuals and read "forecast = spent / over budget"
 * at a third complete — a self-contradiction. BAC/CPI cannot mirror spend: it equals AC
 * only when the work done is worth exactly what it cost. A figure that cannot be computed
 * (no spend yet → no CPI; no plan window → no PV/SPI) is left null rather than faked.
 */
export interface EarnedValue {
  bac: number;
  ev: number;
  /** Actual cost — the COST BASIS for CPI/EAC/ETC: money actually SPENT, not committed. */
  ac: number;
  /** Committed but not yet spent — shown BESIDE the cost read, never folded into the ratio. */
  committed: number;
  pv: number | null;
  cpi: number | null;
  spi: number | null;
  /** The headline estimate at completion — the recent-pace figure when the trend is readable AND
   *  representative, otherwise the classic BAC/CPI. {@link eacBasis} says which. */
  eac: number | null;
  /** The classic BAC/CPI estimate at completion (LIFETIME cost performance) — always present when
   *  there is an EAC, as the stable anchor of the forecast RANGE shown beside the recent pace. */
  eacCpi: number | null;
  /** The recent-pace estimate at completion (remaining worth costed at the RECENT spend rate).
   *  Present only when the recent window is readable AND representative; null when the trend is
   *  held, in which case the surface shows the CPI figure alone. When present it is the headline
   *  `eac`, and `eacCpi` is shown beside it as the range. */
  eacTrend: number | null;
  etc: number | null;
  /** On cost / drifting / over cost — green ≥0.95, amber 0.85–0.95, red below. Null with no CPI. */
  cpiRag: Rag | null;
  /** On schedule / slipping / behind — green ≥0.95, amber 0.85–0.95, red below. Null with no SPI. */
  spiRag: Rag | null;
  /** The %-complete this read is computed at, so a surface can say "project is N% complete". */
  pct: number;
  /** Enough of the project is done (and spent) to forecast cost: `pct ≥ EVM_COST_FORECAST_MIN_PCT`
   *  with spend to read. Below it, cpi/eac/etc/cpiRag are null and the cost forecast is HELD —
   *  BAC/CPI from a tiny early sample is a wild guess, not a number to steer by. */
  costReady: boolean;
  /** HOW the EAC was projected (29 Oct, Vinnie): `trend` = the remaining worth costed at the
   *  RECENT spend rate (where spending is actually heading); `cpi` = the textbook BAC/CPI at the
   *  LIFETIME cost performance, used when the dated spend history is too thin to read a trend.
   *  `manager` = the manager's own hand-set forecast, used when the paid invoices are too thin
   *  against the earned value to read cost from (F1) — so the verdict follows the human estimate,
   *  not a false-rosy CPI. `fixed` = the agreed contract commitment (contract value + approved
   *  adjustments), used on a FIXED-PRICE project where the final cost is capped by contract and
   *  CPI only reflects payment timing — so payment running ahead of earned value can never read as
   *  an overrun. Null when there is no EAC (cost forecast held). The surface names the basis so the
   *  figure is never mistaken for the other. */
  eacBasis: 'trend' | 'cpi' | 'manager' | 'fixed' | null;
}

/** What the trend-aware EAC needs beyond the totals: the recent spend rate and the pace value is
 *  being earned at, both per WORKING day. The caller reads spend from the dated ledger
 *  (src/cost-trend.ts) and value from EV ÷ elapsed working days, and passes this only when the
 *  spend-history floor is cleared; `earnedValue` falls back to BAC/CPI when it is absent. */
export interface EacTrendInput {
  spendPerWorkingDay: number;
  valuePerWorkingDay: number;
}

/** Below this %-complete, a CPI-extrapolated EAC is a wild guess from a tiny earned sample
 *  (a TT$480k budget read a TT$3.6M EAC at 6% complete). Hold the cost forecast until here —
 *  ~15% is the common EVM stability floor (Vinnie, 29 Oct). */
export const EVM_COST_FORECAST_MIN_PCT = 15;

/** The OTHER end of the cost-plausibility guard (F1, Vinnie 1 Nov). Once a project is mature
 *  enough to read cost (past the floor above), a CPI can still be a fantasy in the opposite
 *  direction: when the paid invoices are far BELOW the value already earned, the money simply
 *  has not been invoiced yet, so a sky-high CPI reads a false "miles under budget". Below this
 *  fraction of AC-to-EV the invoice record is treated as too thin to read cost from, and the
 *  verdict falls back to the manager's own forecast instead. 0.5 = spend under half the earned
 *  value is implausibly low; Vinnie-tunable, like the floor above. */
export const AC_EV_PLAUSIBLE_MIN = 0.5;

/** Grade a performance index on the budget signal's own tolerance, so the EVM read and the
 *  budget Health pill can never disagree: ≥0.95 is good, 0.85–0.95 drifting, below is off. */
function indexRag(index: number): Rag {
  if (index >= 0.95) return 'green';
  if (index >= 0.85) return 'amber';
  return 'red';
}

/**
 * The earned-value read for a project, or null when there is no baseline budget or no
 * progress to earn against — in which case the Portfolio shows no EVM, the same way
 * `budgetSignal` returns null rather than inventing a grade.
 */
export function earnedValue(
  bac: number | undefined,
  pctComplete: number | null,
  expectedPct: number | null,
  committed: number,
  actual: number,
  trend?: EacTrendInput,
  // The manager's hand-set forecast (project.forecast). Used only as the FALLBACK verdict when
  // the paid invoices are too thin against earned value to read cost from (F1); ignored otherwise.
  managerForecast?: number,
  // The contract read (Vinnie, 2 Nov). On a FIXED-PRICE project the final cost is the agreed
  // contract commitment, not BAC/CPI — paying a deposit or milestone ahead of earned value pulls
  // CPI below 1 and must never read as an overrun on a capped contract. `basis` is the project's
  // cost basis; `contractValue` is the current contracted total (the store keeps it in step with
  // the latest approved adjustment, store.ts); `adjustments` is the approved-change log, read only
  // to recover the current contracted total if `contractValue` ever drifted from it.
  contract?: { basis?: CostBasis; contractValue?: number; adjustments?: readonly { to: number }[] },
): EarnedValue | null {
  if (bac == null || bac <= 0) return null; // no baseline — nothing to measure against
  if (pctComplete == null) return null; // no progress read — no earned value
  const ev = bac * (pctComplete / 100);
  const pv = expectedPct != null ? bac * (expectedPct / 100) : null;
  const spi = pv != null && pv > 0 ? ev / pv : null; // schedule reads from day one — PV isn't tiny
  // (B, Vinnie 29 Oct) COST basis is ACTUAL SPEND only. Committed money is for work not yet
  // earned; folding it into AC against a small early EV craters CPI and makes EAC explode.
  // Committed is carried separately, as a commitment, never inside the cost ratio.
  const cpiRaw = actual > 0 ? ev / actual : null;
  // (F1, Vinnie 1 Nov) The HIGH-CPI end of the plausibility guard: past the readiness floor, if
  // the paid spend is far below the value already earned, the money is simply not invoiced yet —
  // a sky-high CPI that reads a false "miles under budget". Treat the cost read as unreadable and
  // steer by the manager's forecast instead (below), never by that fantasy CPI.
  const acImplausiblyLow =
    cpiRaw != null && pctComplete >= EVM_COST_FORECAST_MIN_PCT && ev > 0 && actual < AC_EV_PLAUSIBLE_MIN * ev;
  // (A, Vinnie 29 Oct) HOLD the cost forecast + verdict below EVM_COST_FORECAST_MIN_PCT:
  // an extrapolated EAC from a tiny early earned sample is a wild guess, not a number to steer by.
  const costReady = cpiRaw != null && pctComplete >= EVM_COST_FORECAST_MIN_PCT && !acImplausiblyLow;
  const cpi = costReady ? cpiRaw : null;

  // THE EAC — estimate at completion (29 Oct, Vinnie: it must be a realistic projection, "it
  // cannot be 200% unless it is actually going into that direction based on spending"). Two bases:
  //   trend  the remaining worth (BAC − EV) is turned into remaining working days at the pace
  //          value has been EARNED, then costed at the RECENT spend rate — so the estimate follows
  //          where spending is heading, not a lifetime average a single early invoice can dominate.
  //   cpi    the textbook BAC/CPI, i.e. the remaining worth at the LIFETIME cost-per-value. Used
  //          when the dated spend history is too thin to read a recent rate (trend absent), so the
  //          estimate is never a guess dressed as a trend. Note BAC/CPI ≡ AC + (BAC−EV)×AC/EV, so
  //          the trend basis is exactly this with the lifetime spend rate swapped for the recent one.
  // Two bases, always both computed when there is a forecast, so the surface can show a RANGE
  // rather than a lone point (29 Oct, Vinnie / pm-witness): the classic BAC/CPI is the stable
  // anchor; the recent-pace figure is the scenario, used as the HEADLINE only when the caller
  // judged the recent window representative (it passes `trend` only then). A single number on
  // the more aggressive basis, with no spread, is how a forecast misleads.
  let eacCpi: number | null = null;
  let eacTrend: number | null = null;
  if (cpi != null && cpi > 0) {
    const remainingValue = Math.max(0, bac - ev);
    if (remainingValue <= 0) {
      eacCpi = actual; // fully earned — what it cost is what it will cost
    } else {
      eacCpi = bac / cpi;
      if (trend && trend.spendPerWorkingDay > 0 && trend.valuePerWorkingDay > 0) {
        const remainingDays = remainingValue / trend.valuePerWorkingDay;
        eacTrend = actual + trend.spendPerWorkingDay * remainingDays;
      }
    }
  }
  let eac = eacTrend ?? eacCpi;
  let eacBasis: 'trend' | 'cpi' | 'manager' | 'fixed' | null = eac == null ? null : eacTrend != null ? 'trend' : 'cpi';
  // (F1) When the invoices were too thin to read cost (acImplausiblyLow, so cpi/eac are null here)
  // but the manager has set a forecast, steer by that — the verdict then follows the human
  // estimate and reads over budget when the forecast tops the baseline, instead of a false green.
  if (eac == null && acImplausiblyLow && managerForecast != null && managerForecast > 0) {
    eac = managerForecast;
    eacBasis = 'manager';
  }
  // FIXED-PRICE (Vinnie, 2 Nov) — the final cost of a fixed-price job is the agreed contract
  // commitment, not a projection from spend. CPI below 1 from paying a deposit/milestone ahead of
  // earned value is payment TIMING, not an overrun, and the contract caps the cost: it cannot
  // exceed the baseline except by a deliberate, approved scope change (which moves the contract
  // value, and with it this figure). So on a fixed basis the EAC IS the current contracted total —
  // the latest approved adjustment's value, else the stored contract value, else the baseline — and
  // it overrides any CPI/trend/manager EAC above. CPI stays computed as a dimmed SUPPORTING read
  // (the tile dims it via costBasisRead); it is never the headline forecast here. budgetOutcomeRag
  // then reads this against the baseline, so "over budget" fires only on a real scope change that
  // tops it, never on payment timing.
  if (contract?.basis === 'fixed') {
    const adj = contract.adjustments;
    const latest = adj && adj.length > 0 ? adj[adj.length - 1]?.to : undefined;
    const contractTotal = latest ?? contract.contractValue ?? bac;
    if (contractTotal != null && contractTotal > 0) {
      eac = contractTotal;
      eacBasis = 'fixed';
    }
  }
  const etc = eac != null ? Math.max(0, eac - actual) : null;
  return {
    bac,
    ev,
    ac: actual,
    committed,
    pv,
    cpi,
    spi,
    eac,
    eacCpi,
    eacTrend,
    etc,
    cpiRag: cpi != null ? indexRag(cpi) : null,
    spiRag: spi != null ? indexRag(spi) : null,
    pct: pctComplete,
    costReady,
    eacBasis,
  };
}

/**
 * ASSEMBLE A PROJECT'S EARNED-VALUE READ from its live inputs — the one place the trend-aware
 * EAC input is built (the paid-invoice spend rate + value-per-working-day) and handed to
 * earnedValue. Shared so the Portfolio, the status drill AND the Plan all read ONE derived EAC
 * (SB-1, Q1 — "Plan+Portfolio match the drill"), instead of each surface computing its own or,
 * as the Plan tile did, falling back to the hand-typed forecast and disagreeing with the rest.
 * Pure; `pp` and `expectedThroughPlan` are passed in so a caller that already has them (the
 * portfolio row) does not compute them twice.
 */
export function projectEvm(args: {
  project: Project;
  pp: { counted: number; pct: number };
  expectedThroughPlan: number | null;
  roll: { committedTotal: number; actualTotal: number };
  now: number;
  cal: WorkCalendar;
  ctx?: BudgetContext;
}): EarnedValue | null {
  const { project, pp, expectedThroughPlan, roll, now, cal, ctx } = args;
  const startDay = startDayOf(project);
  const bac = project.budget;
  let trendInput: EacTrendInput | undefined;
  if (bac != null && bac > 0 && startDay && pp.counted && pp.pct > 0) {
    const nowISO = new Date(now).toISOString().slice(0, 10);
    const elapsedWorkingDays = workingDaysInclusive(startDay, nowISO, cal);
    const ev = bac * (pp.pct / 100);
    const st = spendTrend(paidSpendSteps(project.invoices ?? [], ctx), now, cal);
    // Steer by the recent rate only when the window is both readable and representative — else a
    // catch-up-invoicing burst reads as false acceleration (the EAC blows past the honest BAC/CPI).
    if (st.readable && st.representative && elapsedWorkingDays > 0 && ev > 0) {
      trendInput = { spendPerWorkingDay: st.perWorkingDay, valuePerWorkingDay: ev / elapsedWorkingDays };
    }
  }
  return earnedValue(
    bac,
    pp.counted ? pp.pct : null,
    expectedThroughPlan,
    roll.committedTotal,
    roll.actualTotal,
    trendInput,
    project.forecast,
    { basis: project.costBasis, contractValue: project.contractValue, adjustments: project.contractAdjustments },
  );
}

/** Grade a projected cost OUTCOME — the estimate at completion against the baseline (VAC = BAC −
 *  EAC). Landing at or under baseline is green; ANY overrun down-ranks off green to amber (Vinnie,
 *  1 Nov — the moment the forecast tops the budget the pill must stop reading "on track"); a big
 *  overrun is red. Shared by the live read and the pulse writer so the recorded history and the
 *  live pill can never disagree wherever they read the same EAC. */
export function budgetOutcomeRag(eac: number, bac: number): Rag {
  const over = eac / bac - 1;
  if (over <= 0.005) return 'green';
  if (over <= 0.15) return 'amber';
  return 'red';
}

/**
 * THE BUDGET HEALTH PILL, from the projected OUTCOME (will we land within budget?), not cost
 * efficiency to date. Vinnie, 1 Nov (L192): a steering budget verdict must answer "does the
 * forecast land inside the baseline?" — graded on the estimate at completion, not CPI. CPI heals
 * toward 1.00 as the work wraps and, on fixed-price spend, reflects payment timing rather than
 * efficiency, so a CPI-graded pill read green ("on cost · 0.98") beside a 6% forecast overrun.
 * The CPI index is still shown on the tile, as a supporting number with its own caveat. Graded
 * on the headline EAC (the recent-pace figure when the window is representative, else the classic
 * BAC/CPI) so colour and the shown variance can never contradict. Null when the cost forecast is
 * held — no baseline, no progress, nothing spent, or below the readiness floor. The daily pulse
 * writer mirrors this on the classic cost-to-date EAC it can compute (BAC/CPI), which equals this
 * EAC wherever a recent-pace rate is not read — so the recorded history and the pill agree.
 */
export function budgetHealth(evm: EarnedValue | null): Signal | null {
  if (!evm || evm.eac == null || evm.bac <= 0) return null;
  // Grade when we have a trustworthy invoice-based read (costReady), OR when the read was held but
  // the verdict fell back to the manager's forecast (F1), OR when it is a fixed-price contract
  // whose EAC is the agreed contract value (always a valid verdict, read from the contract not the
  // thin spend) — never leave a forecast-over-baseline reading as no-pill, which elsewhere reads
  // as "fine".
  if (!evm.costReady && evm.eacBasis !== 'manager' && evm.eacBasis !== 'fixed') return null;
  const rag = budgetOutcomeRag(evm.eac, evm.bac);
  const over = evm.eac / evm.bac - 1;
  const why = rag === 'red' ? 'heading well over budget'
    : rag === 'amber' ? 'trending over budget'
    : over < -0.03 ? 'projected under budget' : 'on budget';
  return { rag, why };
}

/** What a project's cost basis is called on screen. */
export const COST_BASIS_LABELS: Record<CostBasis, string> = {
  fixed: 'Fixed price',
  tm: 'Time & materials',
  mixed: 'Mixed',
};

/**
 * HOW THE COST SIDE SHOULD READ for a project's cost basis — the honest-reporting
 * tuning the fixed-price research called for (1 Nov). On a FIXED-PRICE project the
 * spend follows the payment schedule, so CPI is not an efficiency signal: the cost
 * tile leads with commitment-vs-budget exposure and the forecast verdict, and the
 * CPI index is shown only faintly with its payment-timing caveat. On TIME & MATERIALS
 * the spend tracks effort, so CPI IS meaningful and reads at normal weight without the
 * fixed-price caveat. MIXED or unset keeps the balanced read (verdict leads, CPI
 * supports, exposure promoted only once commitment is high). Pure; the cost tile
 * consumes the flags and owns the copy.
 */
export interface CostRead {
  /** CPI is a genuine efficiency read (true only for time & materials). */
  cpiMeaningful: boolean;
  /** Lead the tile with commitment-vs-budget exposure regardless of how much is
   *  committed yet (true for fixed price — the exposure, not CPI, is the real read). */
  leadWithExposure: boolean;
}
export function costBasisRead(cb?: CostBasis): CostRead {
  if (cb === 'tm') return { cpiMeaningful: true, leadWithExposure: false };
  if (cb === 'fixed') return { cpiMeaningful: false, leadWithExposure: true };
  return { cpiMeaningful: false, leadWithExposure: false };
}

/**
 * BLOCKERS — the oldest open blocker's age. None, or all under a week, reads
 * green; over a week amber; over two weeks red. Returns the grade and the open
 * items, oldest first, so the Waiting-on column reads from the same list.
 */
export function blockersSignal(
  issues: Issue[],
  now = Date.now(),
  // The blocker's target is a raw handle (often an email); resolve it to a name
  // once, HERE, so every consumer (roll-up, drill, blocked-cards, by-person)
  // shows a name and never a raw address — the same rule the owner column follows.
  resolveName: (who: string) => string = (who) => who,
): Signal & { waiting: WaitingItem[] } {
  const open = reallyBlocked(issues);
  const waiting: WaitingItem[] = open
    .map((i) => {
      const targetRaw = i.blockerTarget?.trim();
      const target = targetRaw ? resolveName(targetRaw) : undefined;
      return {
        type: (i.blockerType ?? '') as BlockerType | '',
        label: blockerOf(i) || target || 'something',
        target,
        days: ageInDays(i.blockedAt, now),
        ref: i.ref,
        title: i.title,
      };
    })
    .sort((a, b) => (b.days ?? 0) - (a.days ?? 0));

  if (waiting.length === 0) return { rag: 'green', why: 'none', waiting };
  const oldest = waiting[0].days ?? 0;
  let rag: Rag = 'green';
  if (oldest > 14) rag = 'red';
  else if (oldest > 7) rag = 'amber';
  const why =
    waiting.length === 1 ? `1 open · ${oldest}d` : `${waiting.length} open · oldest ${oldest}d`;
  return { rag, why, waiting };
}

/** Cards DONE across the live phases as a simple count-based 0–100 (done ÷ counted).
 *  NOTE: this is the done-COUNT, not the headline project %. The project's displayed
 *  "% complete" everywhere (Plan, rollup, status, EVM) is the WEIGHTED projectProgress()
 *  below, which credits partly-done cards. Kept as a plain done-count helper. */
export function progressOf(phases: Phase[], issues: Issue[]): number {
  let total = 0;
  let done = 0;
  for (const p of livePhases(phases)) {
    const c = phaseCardCounts(issues, p.id);
    total += c.total;
    done += c.done;
  }
  return total ? Math.round((done / total) * 100) : 0;
}

/**
 * THE HEADLINE PROJECT % (#progress) — count-weighted and granular across every
 * non-cancelled card on the board. Each card weighs the same (count-weighted, the
 * call Vinnie made): a done card counts 100, an open card the share it records
 * (its typed %, or — via `doneChecks` — how much of the board's definition of done
 * it has ticked), cancelled work (won't do) left out entirely. Held below 100
 * unless every counted card is done, the same "100 iff done" invariant the phase
 * and epic bars keep. `allDone` is returned alongside so the schedule read can say
 * "Done" rather than invent a lateness on a finished project.
 */
export function projectProgress(issues: Issue[], doneChecks: readonly string[]): { pct: number; allDone: boolean; counted: number } {
  let counted = 0;
  let done = 0;
  let sum = 0;
  for (const i of issues) {
    if (i.status === 'wontdo') continue;
    counted += 1;
    if (i.status === 'done') {
      done += 1;
      sum += 100;
      continue;
    }
    sum += Math.min(Math.max(effectiveProgressPct(i, doneChecks) ?? 0, 0), 99);
  }
  const allDone = counted > 0 && done === counted;
  const pct = counted === 0 ? 0 : allDone ? 100 : Math.min(Math.round(sum / counted), 99);
  return { pct, allDone, counted };
}

const WEEK_MS = 7 * 86_400_000;

/** Where a status sits on the board, for judging a move's direction. Both
 *  finished statuses share the top rank, so done → won't-do is not a "push
 *  back", but any drop from a working column — or a reopen out of a finished
 *  one — is. Mirrors STATUS_ORDER without importing its exact indices. */
const STATUS_RANK: Record<IssueStatus, number> = {
  backlog: 0, todo: 1, inprogress: 2, staging: 3, inreview: 4, done: 5, wontdo: 5,
};

/**
 * HOW WORK MOVED since a reference date — the directional flow, read off the
 * card transition log (`activity`, which records who moved a card from which
 * status to which, for every writer). `started` and `toReview` count forward
 * entries into those columns; `pushedBack` counts any backward move, including a
 * card reopened out of done/won't-do (Vinnie's call). `closed` is the card's own
 * DB-stamped finish (`completedAt`) so it agrees with the throughput trend and
 * survives boards with no structured transition history; `newBlockers` reads the
 * blocker stamp the same way it always has. A board with no transition history
 * yet degrades honestly — the log-based verbs read zero rather than inventing a
 * move.
 */
export function movementSince(issues: Issue[], sinceMs: number, now = Date.now()): MovementCounts {
  let started = 0;
  let staged = 0;
  let toReview = 0;
  let pushedBack = 0;
  for (const i of issues) {
    for (const a of i.activity ?? []) {
      if (a.toStatus == null || a.at < sinceMs || a.at > now) continue;
      const to = STATUS_RANK[a.toStatus];
      const from = a.fromStatus != null ? STATUS_RANK[a.fromStatus] : to;
      if (to < from) { pushedBack += 1; continue; }
      if (a.toStatus === 'inprogress') started += 1;
      else if (a.toStatus === 'staging') staged += 1;
      else if (a.toStatus === 'inreview') toReview += 1;
    }
  }
  const closed = issues.filter((i) => i.completedAt != null && i.completedAt >= sinceMs && i.completedAt <= now).length;
  const newBlockers = issues.filter(
    (i) => i.blockedAt != null && i.blockedAt >= sinceMs && (i.unblockedAt == null || i.unblockedAt < i.blockedAt),
  ).length;
  return { started, staged, toReview, closed, pushedBack, newBlockers };
}

/** The movement counts as the plain sentence-case verbs the roll-up shows,
 *  zero terms omitted, in flow order. Empty when nothing moved — the cell then
 *  reads "No movement". */
export function movementVerbs(m: MovementCounts): string[] {
  const out: string[] = [];
  if (m.started) out.push(`${m.started} started`);
  if (m.staged) out.push(`${m.staged} to staging`);
  if (m.toReview) out.push(`${m.toReview} to review`);
  if (m.closed) out.push(`${m.closed} closed`);
  if (m.pushedBack) out.push(`${m.pushedBack} pushed back`);
  if (m.newBlockers) out.push(`${m.newBlockers} new ${m.newBlockers === 1 ? 'blocker' : 'blockers'}`);
  return out;
}

/** The verbs a reference date produces directly — kept for the roll-up cell and
 *  the pure tests. */
export function movedSince(issues: Issue[], sinceMs: number, now = Date.now()): string[] {
  return movementVerbs(movementSince(issues, sinceMs, now));
}

/**
 * THE CLOSED-PER-WEEK TREND — how many cards finished in each of the last
 * `weeks` weeks, read from `completedAt` (DB-stamped, so honest). The last bin is
 * this week; `norm` is the average of the earlier weeks, the line this week is
 * judged against. Undefined when nothing has closed across the window — there is
 * then no line to draw and no norm, and the row reads "No movement" instead.
 */
export function throughputOf(issues: Issue[], now = Date.now(), weeks = 6): Throughput | undefined {
  const weekly = new Array<number>(weeks).fill(0);
  for (const i of issues) {
    if (i.completedAt == null) continue;
    const ago = now - i.completedAt;
    if (ago < 0 || ago >= weeks * WEEK_MS) continue;
    const bin = weeks - 1 - Math.floor(ago / WEEK_MS);
    if (bin >= 0 && bin < weeks) weekly[bin] += 1;
  }
  if (weekly.every((n) => n === 0)) return undefined;
  const thisWeek = weekly[weeks - 1];
  const prior = weekly.slice(0, weeks - 1);
  const norm = prior.length ? prior.reduce((a, b) => a + b, 0) / prior.length : 0;
  const dir: Throughput['dir'] = thisWeek > norm + 0.5 ? 'up' : thisWeek < norm - 0.5 ? 'down' : 'flat';
  return { weekly, thisWeek, norm: Math.round(norm * 10) / 10, dir };
}

/** Too few finished cards to read a pace from — below this the go-live-pace lens stays quiet, the
 *  same way the drill shows no pace band until its forecast is `ready` (a handful of finishes). */
const GOLIVE_PACE_MIN_FINISHED = 4;

/**
 * F2 (Vinnie, 2 Nov) — a CHEAP, deterministic go-live concern for the roll-up LIST.
 *
 * A project whose committed phase-dates read fine but whose PACE will miss the go-live used to
 * sit GREEN in the list while its own drill read "late" — the list health folded in schedule,
 * blockers, progress-vs-clock and budget, but NOT the pace/go-live forecast (that lived only in
 * the drill's Monte-Carlo). This folds a coarse pace COLOUR into the list so the two can no
 * longer disagree in the dangerous direction. The drill keeps the dated p85 prediction; the list
 * carries only this colour. No trials and no per-row forecast — it reads the board's recent
 * throughput against the open work still to clear before the plan day, so it costs O(cards) per
 * row, never the 1000-row Monte-Carlo.
 *
 * It leans CONSERVATIVE so it never UNDER-warns relative to the drill (coarser / over-warning is
 * acceptable, under-warning is not — Vinnie's guard):
 *   - A board that HAS finished work but has since STALLED (no recent throughput) while open work
 *     and a future go-live remain reads RED — it is not on course to land.
 *   - A board on pace needs a comfortable margin — a 20% cushion standing in for the p85 spread —
 *     to read green; "only just makes it" is a warn, not green.
 *   - A board with too little finishing history to read a pace stays GREEN here (the schedule
 *     signal still catches an overdue go-live), matching the drill's "no pace band until ready".
 */
export function goLivePaceRag(
  planDay: string | undefined,
  openRemaining: number,
  finishedCount: number,
  throughput: Throughput | undefined,
  now: number,
): Rag {
  if (!planDay || openRemaining === 0) return 'green';
  if (finishedCount < GOLIVE_PACE_MIN_FINISHED) return 'green'; // too thin to read a pace (drill parity)
  const planMs = Date.parse(`${planDay}T00:00:00Z`);
  const haveWeeks = (planMs - now) / WEEK_MS;
  if (haveWeeks <= 0) return 'green'; // plan here/past — scheduleSignal's overdue read owns that colour
  // Recent weekly finishing rate. A board that finished before but not inside the throughput
  // window reads 0 here — stalled — and with open work and a go-live still ahead it will miss.
  const perWeek = throughput ? Math.max(throughput.norm, throughput.thisWeek) : 0;
  if (perWeek <= 0) return 'red';
  const ratio = openRemaining / perWeek / haveWeeks; // >1 — today's pace does not clear the work by the plan
  if (ratio > 1.5) return 'red';
  if (ratio > 0.8) return 'amber'; // the 20% cushion: a bare finish is a warn, not green
  return 'green';
}

/** THE 28-DAY FLOW READ (#435 surface 2) — what an in-service board is judged on
 *  instead of a schedule: cards finished per week, and the net backlog change
 *  (raised − finished; negative means it is closing faster than arriving). Keyed
 *  off `completedAt`/`createdAt`, the same honest clocks throughput and keepingUp
 *  use, so the portfolio can never disagree with the board strip. Null when nothing
 *  has moved in the window — there is then no flow to read. */
const FLOW_WINDOW_DAYS = 28;
function flow28Of(issues: Issue[], now: number): { perWeek: number; net: number } | undefined {
  const since = now - FLOW_WINDOW_DAYS * 86_400_000;
  let finished = 0;
  let raised = 0;
  for (const i of issues) {
    if (i.completedAt != null && i.completedAt >= since && i.completedAt <= now) finished += 1;
    if (i.createdAt != null && i.createdAt >= since && i.createdAt <= now) raised += 1;
  }
  if (finished === 0 && raised === 0) return undefined;
  const perWeek = Math.round((finished / (FLOW_WINDOW_DAYS / 7)) * 10) / 10;
  return { perWeek, net: raised - finished };
}

/** The in-flight cards with their age against the board's own norm, oldest
 *  first — the aging-WIP signal (card-age.ts) turned into a portfolio list. The
 *  norm is measured once over what is in flight, so every card is judged against
 *  the same line. */
export function agingCards(issues: Issue[], now = Date.now()): AgingCard[] {
  const threshold = agingThreshold(workingAges(issues, now));
  const out: AgingCard[] = [];
  for (const i of issues) {
    if (!isWorking(i)) continue;
    const days = ageInDays(i.startedAt, now);
    if (days === null) continue;
    out.push({ ref: i.ref, title: i.title, days, aged: days > threshold });
  }
  return out.sort((a, b) => b.days - a.days);
}

/** A one-line reason for the health pill — the loud signals, joined ("blocked +
 *  late"). Reads only the grades, so it never disagrees with the pill. */
function healthWhyOf(schedule: Signal, budget: Signal | null, blockers: Signal, progRag: Rag, golivePace: Rag): string {
  const bits: string[] = [];
  if (blockers.rag !== 'green') bits.push('blocked');
  // The three schedule-family lenses, in order of specificity — show ONE so the chip stays
  // tight: committed dates slipping ('late') beats the pace missing the go-live ('behind
  // pace', F2) beats the work trailing the clock ('behind schedule'). Never more than one.
  if (schedule.rag !== 'green') bits.push('late');
  else if (golivePace !== 'green') bits.push('behind pace');
  else if (progRag !== 'green') bits.push('behind schedule');
  if (budget && budget.rag !== 'green') bits.push('over budget');
  return bits.join(' + ') || 'on track';
}

/**
 * ONE PROJECT'S ROW — the three signals combined into a health, plus the figures
 * each column shows. `issues` and `phases` are this board's own; `sinceMs` is the
 * reference date the "Moved" column reads against (the last pack, or a week ago).
 */
export function portfolioRow(
  project: Project,
  issues: Issue[],
  phases: Phase[],
  ctx?: BudgetContext,
  now = Date.now(),
  sinceMs = now - 7 * 86_400_000,
  /** Turns whoever-is-responsible into a shown name. The owner is a project
   *  manager's typed name, or — most boards — the creator's email; this resolves
   *  an email to that person's name so the column never shows a raw address.
   *  Defaults to identity so the pure layer stays testable without profiles. */
  resolveName: (who: string) => string = (who) => who,
  /** This board's charter Risk register, already read to rows (charter.ts) by the
   *  caller — the pure layer never reads a document itself. Empty on a project
   *  with no authored register. */
  charterRisks: CharterRisk[] = [],
  /** This board's own issue types, so each card's Run-vs-Change class derives
   *  through the SAME type→means map the board uses. Empty → every card falls to
   *  its derived default (upkeep → Run), so an old caller that cannot supply types
   *  still builds a row rather than throwing. */
  types: IssueType[] = [],
  /** This board's definition of done (ProjectMeta.doneChecks), so a card with no
   *  typed % still contributes its ticked share to the Project %. Empty → the
   *  Project % reads typed %s only. The pure layer never reads the board itself. */
  doneChecks: readonly string[] = [],
  /** The workspace working calendar (0216), so "expected %" and each card's health
   *  count only working days — a known close doesn't make the plan read behind.
   *  Defaults to Mon–Fri with no holidays, i.e. plain working days. */
  cal: WorkCalendar = DEFAULT_CALENDAR,
  /** This board's milestone register (standalone rows), so a go-live anchored to a milestone
   *  (0217) reads that milestone's planned day. Empty → phase/fixed anchors still resolve; a
   *  milestone anchor falls back to the typed date. */
  milestones: readonly MilestoneRow[] = [],
): PortfolioRow {
  // Resolve a go-live anchored to a milestone (0217) to that milestone's baseline day, so the
  // portfolio reads the same anchored date the Plan does.
  const milestoneDay = (id: string): string | null | undefined =>
    milestones.find((m) => m.id === id)?.targetDay;
  const planAnchored = goLiveIsPlanAnchored(project.goLiveAnchor);
  const schedule = scheduleSignal(project, phases, issues, now, milestoneDay);
  const blockers = blockersSignal(issues, now, resolveName);
  const movement = movementSince(issues, sinceMs, now);
  const onHold = !!project.heldAt;

  const roll = rollupBudget(project, ctx);
  // Progress and the earned-value read come BEFORE the budget signal: the budget Health
  // pill and the variance are driven by the DERIVED EAC (Vinnie, 29 Oct) — from the
  // budget, the Project %, the planned-%-by-now and the spend — not the hand-typed
  // forecast, so cost health can never mirror spend-to-date or go stale.
  const pp = projectProgress(issues, doneChecks);
  const expectedThroughPlan = expectedPctOfWindow(startDayOf(project), schedule.planDay, now, cal);
  const progressSchedule = scheduleHealth(pp.counted ? pp.pct : null, expectedThroughPlan, pp.allDone);

  // THE DERIVED EAC (29 Oct / SB-1) — assembled once in projectEvm (shared with the Plan), which
  // builds the trend-aware input off the dated PAID-invoice ledger and hands it to earnedValue.
  const startDay = startDayOf(project);
  // Card-progress-vs-clock as a third schedule severity, folded into the project's
  // health so the roll-up pill can never read green beside its own "About N% behind
  // schedule" line, and the list agrees with the drill report (finding 3, Vinnie
  // 29 Oct). Needs no finished-work history, so it bites in cold-start too.
  const progRag = scheduleHealthSeverity(progressSchedule);
  const evm = projectEvm({ project, pp, expectedThroughPlan, roll, now, cal, ctx });
  const budget = budgetHealth(evm);

  // EARNED SCHEDULE — the date-truth read (Layer C, Surface 6A). The plan curve and
  // the earned % it is read against share ONE basis: the cards that carry a planned
  // due, count-weighted exactly as projectProgress weights them (each non-cancelled
  // card weight 1), so SPI(t) is internally consistent — never a whole-project % held
  // up against a dated-only plan. `es.scheduled` discloses how many cards that is, and
  // undated work stays Layer A's to chase (a date is never guessed here). Held when
  // there is no start, no committed finish, or nothing dated to read.
  const scheduledCards = issues.filter((i) => i.status !== 'wontdo' && typeof i.dueDate === 'string' && i.dueDate.trim() !== '');
  const datedPp = projectProgress(scheduledCards, doneChecks);
  const earned =
    startDay && schedule.planDay && datedPp.counted > 0
      ? earnedSchedule({
          plan: scheduledCards.map((i) => ({ due: i.dueDate as string, weight: 1 })),
          earnedPct: datedPp.pct,
          startISO: startDay,
          goLiveISO: schedule.planDay,
          now,
          cal,
        })
      : null;
  const variance =
    evm && evm.eac != null && project.budget != null && project.budget > 0
      ? { diff: evm.eac - project.budget, pct: ((evm.eac - project.budget) / project.budget) * 100 }
      : undefined;

  const phaseRows = livePhases(phases).map((p) => {
    const shown = shownStatusOf(p, issues);
    return {
      id: p.id,
      name: p.name,
      status: shown,
      goLive: goLiveState(p, schedule.planDay, shown, now, planAnchored),
      ...(p.targetEnd ? { targetEnd: p.targetEnd } : {}),
    };
  });

  // THE GO-LIVE PACE LENS (F2) — a coarse, deterministic read of whether the board's recent
  // throughput clears its open work by the plan day, so a late/stalled project can no longer sit
  // green in the list while its drill reads "late". Reuses the row's own throughput (no trials).
  const tp = onHold ? undefined : throughputOf(issues, now);
  const openRemaining = issues.filter((i) => i.status !== 'done' && i.status !== 'wontdo' && i.status !== 'backlog').length;
  const finishedCount = issues.filter((i) => i.status === 'done').length;
  const golivePace: Rag = onHold ? 'green' : goLivePaceRag(schedule.planDay, openRemaining, finishedCount, tp, now);

  // On hold hushes the colour but not the waiting list — a parked project still
  // says why it is parked. Its health reads as no colour (onHold carries it).
  const health = onHold ? null : worst([schedule.rag, blockers.rag, progRag, golivePace, ...(budget ? [budget.rag] : [])]);

  const waiting: WaitingItem[] = onHold
    ? [
        {
          type: 'parked' as const,
          label: project.holdReason?.trim() || 'parked',
          days: project.heldAt != null ? Math.max(0, Math.floor((now - project.heldAt) / 86_400_000)) : null,
          ...(project.holdPickup?.trim() ? { target: project.holdPickup.trim() } : {}),
        },
        ...blockers.waiting,
      ]
    : blockers.waiting;

  // A project manager's typed name wins; otherwise the board's creator (an email).
  const ownerRaw = project.projectManager?.trim() || project.owner?.trim() || '';

  // WHERE THE BOARD IS IN ITS LIFE (#435 surface 2). In service once it has been
  // handed over (actualGoLive set) OR is run as a continuous service — the explicit
  // signal, not "all phases look done". A tracked project that is neither is in
  // delivery; a bare board is pipeline. A continuous service counts as in service
  // even without a plan, so it is not demoted to pipeline by the tracked heuristic.
  const tracked = onHold || schedule.planDay != null || phaseRows.length > 0 || project.budget != null;
  const atService = isContinuous(project) || isInService(project);
  const tier: LifecycleTier = atService ? 'inservice' : tracked ? 'indelivery' : 'pipeline';
  const overdue = goLiveOverdue({
    deliveryMode: project.deliveryMode,
    tracked,
    actualGoLive: project.actualGoLive,
    // Judge "overdue to go live" against the ANCHOR-resolved go-live day (0217), not the raw
    // typed date, so a phase/milestone-anchored project is measured by the date the plan sets.
    plannedGoLive: schedule.planDay ? Date.parse(`${schedule.planDay}T00:00:00Z`) : project.goLive,
    nowMs: now,
  });
  const flow28 = atService && !onHold ? flow28Of(issues, now) : undefined;

  // The headline Project % (granular, count-weighted) and how it reads against the
  // plan's calendar. The window is the project's own start → planned go-live; with
  // no such window the schedule read carries no signal (kind 'none').
  return {
    id: project.id,
    name: project.name ?? 'Untitled project',
    // The responsible person: the project manager's name if one is set, else the
    // board creator (stored as their email), resolved to a name. Never blank, never
    // a raw address — "Unassigned" when nobody is on the project at all.
    owner: ownerRaw ? resolveName(ownerRaw) : 'Unassigned',
    sponsor: project.sponsor?.trim() || undefined,
    startDay: startDayOf(project),
    onHold,
    holdReason: project.holdReason,
    holdPickup: project.holdPickup,
    // Run as a project the moment it has a plan: a go-live, a phase, or a budget —
    // or once it has been put on hold, which is itself a decision made about a
    // project (you do not park a bare development board).
    tracked,
    health,
    healthWhy: onHold ? 'on hold' : healthWhyOf(schedule, budget, blockers, progRag, golivePace),
    schedule: { rag: schedule.rag, why: schedule.why },
    budget,
    blockers: { rag: blockers.rag, why: blockers.why },
    // THE PROJECT %: the WEIGHTED completion (each open card earns the share it records
    // via its %/checklist/DoD) — the same pp.pct the status report and EVM earned-value use,
    // so the rollup table, the board-signal tagline and the pack PDF never read a different
    // "% complete" than the drill does (OBS-3, 30 Oct — Vinnie: one weighted % everywhere).
    progress: pp.pct,
    projectPct: pp.pct,
    progressSchedule,
    // The per-card %-complete roll-up (#progress) — behind / at-risk counts over
    // the board's open cards that carry a % and a schedule. Held even on hold: the
    // counts describe the work, and a reader still wants to see what is slipping.
    progressCounts: progressCountsOf(issues, phases, now, doneChecks, cal),
    // The cards behind that roll-up, for the status-report drill-down — same engine,
    // so the list and the counts above never disagree.
    progressCards: progressCardsOf(issues, phases, now, doneChecks, cal),
    waiting,
    planDay: schedule.planDay,
    forecastDay: schedule.forecastDay,
    goLiveAnchorLabel: goLiveAnchorLabelOf(project, phases, milestones),
    slipDays: schedule.slipDays,
    budgetBase: project.budget,
    forecast: project.forecast,
    variance,
    committed: roll.committedTotal,
    actual: roll.actualTotal,
    // Earned value from what is already captured — the budget, the Project %, the
    // planned %-by-now and the spend. Computed above (it drives the budget pill too).
    evm,
    earnedSchedule: earned,
    phases: phaseRows,
    // On hold hushes the movement read too — a parked project is not "moving".
    movement,
    moved: onHold ? [] : movementVerbs(movement),
    throughput: tp,
    aging: onHold ? [] : agingCards(issues, now),
    agingNorm: agingThreshold(workingAges(issues, now)),
    risks: project.risks?.trim() || undefined,
    successMeasure: richHasText(project.successMeasure) ? project.successMeasure : undefined,
    charterRisks: charterRisks.length ? charterRisks : undefined,
    // The Run-vs-Change split for this board, by card count. Held even on a held
    // project — the mix is a property of the work, not of whether it is paused.
    mix: boardInvestmentMix(issues, types, investmentLifecycleDefault(tier)),
    // The go-live -> in-service lifecycle (#435 surface 2) — the tier the portfolio
    // bands by, plus the handoff fields carried straight from the board row and the
    // flow/overdue reads each tier is judged on.
    tier,
    ...(project.key?.trim() ? { key: project.key.trim() } : {}),
    ...(project.deliveryMode ? { deliveryMode: project.deliveryMode } : {}),
    ...(project.costBasis ? { costBasis: project.costBasis } : {}),
    ...(project.actualGoLive != null ? { actualGoLive: project.actualGoLive } : {}),
    ...(project.deliverySnapshot ? { deliverySnapshot: project.deliverySnapshot } : {}),
    ...(overdue ? { overdue } : {}),
    ...(flow28 ? { flow28 } : {}),
  };
}

/**
 * THE WHOLE PORTFOLIO — a row per project, from each board's own issues and
 * phases (keyed by board id). Projects with no board data yet still get a row
 * (empty issues/phases → green, 0%). Sorted worst-health first so the meeting
 * starts where the trouble is; held projects sink below the live ones.
 */
export function buildPortfolio(
  projects: Project[],
  issuesByBoard: Map<string, Issue[]>,
  phasesByBoard: Map<string, Phase[]>,
  ctx?: BudgetContext,
  opts?: {
    now?: number;
    sinceMs?: number;
    resolveName?: (who: string) => string;
    /** Each board's charter Risk register, already read to rows (charter.ts).
     *  Optional so a caller with no charters — and the pure tests — still build a
     *  portfolio; a project not in the map simply carries no charter risks. */
    charterRisksByBoard?: Map<string, CharterRisk[]>;
    /** Each board's own issue types, so every board's Run-vs-Change split derives
     *  through its OWN type→means map. Optional: a board not in the map classifies
     *  on the derived default (Run), and the pure tests can omit it. */
    issueTypesByBoard?: Map<string, IssueType[]>;
    /** Each board's definition of done (ProjectMeta.doneChecks), so the Project %
     *  counts a card's ticked checks where it carries no typed %. Optional: a board
     *  not in the map reads typed %s only. */
    doneChecksByBoard?: Map<string, readonly string[]>;
    /** The workspace working calendar (0216) — one for the whole portfolio, so every
     *  row's "expected %" and card health count working days. Omitted → plain Mon–Fri. */
    calendar?: WorkCalendar;
    /** Each board's milestone register (standalone rows), so a go-live anchored to a milestone
     *  (0217) resolves its date. Optional: a board not in the map resolves phase/fixed anchors
     *  and falls back to the typed date for a milestone anchor. */
    milestonesByBoard?: Map<string, MilestoneRow[]>;
  },
): PortfolioRow[] {
  const now = opts?.now ?? Date.now();
  const sinceMs = opts?.sinceMs ?? now - 7 * 86_400_000;
  const resolveName = opts?.resolveName ?? ((who) => who);
  const rows = projects.map((p) =>
    portfolioRow(
      p,
      issuesByBoard.get(p.id) ?? [],
      phasesByBoard.get(p.id) ?? [],
      ctx,
      now,
      sinceMs,
      resolveName,
      opts?.charterRisksByBoard?.get(p.id) ?? [],
      opts?.issueTypesByBoard?.get(p.id) ?? [],
      opts?.doneChecksByBoard?.get(p.id) ?? [],
      opts?.calendar ?? DEFAULT_CALENDAR,
      opts?.milestonesByBoard?.get(p.id) ?? [],
    ),
  );
  const health = (r: PortfolioRow) => (r.onHold ? -1 : RANK[r.health ?? 'green']);
  return rows.sort((a, b) => health(b) - health(a) || a.name.localeCompare(b.name));
}
